import type { DocNode } from '@atlaskit/adf-schema/doc';
import type CardClient from '@atlaskit/link-provider/client';
import type { CardStore } from '@atlaskit/linking-common/store';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { extractSmartLinksFromAdf } from './extractSmartLinksFromAdf.ts';
import { jsonLdToEnhancedSmartLink } from './jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';

type SmartLinkPosition = {
	url: string;
	occurrenceIndex: number;
};

type DraftSmartCard = {
	type?: string;
	attrs?: {
		url?: string;
	};
};

type GlobalWithDraftSmartCards = typeof globalThis & {
	__SMART_CARDS_IN_DRAFT__?: DraftSmartCard[];
};

const getDraftSmartCardsFromWindow = (): DraftSmartCard[] => {
	const draftCards = (globalThis as GlobalWithDraftSmartCards).__SMART_CARDS_IN_DRAFT__;
	return Array.isArray(draftCards) ? draftCards : [];
};

const getDraftSmartLinkPositions = (smartCardsInDraft: DraftSmartCard[]): SmartLinkPosition[] => {
	const seen = new Set<string>();
	const positions: SmartLinkPosition[] = [];
	let counter = 0;

	for (const card of smartCardsInDraft) {
		const type = card?.type;
		const url = card?.attrs?.url;
		const isSmartCardType = type === 'inlineCard' || type === 'blockCard' || type === 'embedCard';
		if (!isSmartCardType || typeof url !== 'string' || url.length === 0 || seen.has(url)) {
			continue;
		}

		seen.add(url);
		positions.push({ url, occurrenceIndex: counter++ });
	}

	return positions;
};

/**
 * Single extract→resolve→map pipeline.
 *
 * 1. Walks the ADF to get ordered { url, occurrenceIndex } positions.
 * 2. For each URL, reads from the SmartCard store first (free, synchronous).
 *    Falls back to CardClient.prefetchData (network) on cache miss.
 * 3. Maps each raw JsonLd.Response through jsonLdToEnhancedSmartLink.
 *
 * Per-URL ORS failures are swallowed (.catch → null) so one broken link
 * does not prevent the rest from resolving.
 */
export async function collectSmartLinks(
	adf: DocNode | null | undefined,
	store: { getState: () => CardStore },
	cardClient: CardClient,
): Promise<EnhancedSmartLink[]> {
	const adfPositions = extractSmartLinksFromAdf(adf);
	// The page ADF document is authoritative for the current page once it is available.
	// Only bridge with the draft snapshot (`__SMART_CARDS_IN_DRAFT__`) while the document is
	// not yet available (`adf == null`); otherwise a brand-new/empty page would surface the
	// previous page's stale draft smart cards, since that window global persists across
	// client-side navigation and is not reset when creating a new page.
	const smartCardsInDraft = getDraftSmartCardsFromWindow();
	const draftPositions = getDraftSmartLinkPositions(smartCardsInDraft);
	const positions = adf ? adfPositions : draftPositions;

	if (positions.length === 0) {
		return [];
	}

	const storeState = store.getState();

	return Promise.all(
		positions.map(async ({ url, occurrenceIndex }) => {
			const cached = storeState[url]?.details;

			const response = cached?.data
				? cached
				: await cardClient.prefetchData(url).catch((err: unknown) => {
						const errorObj = err instanceof Error ? err : new Error(String(err));
						getMonitoringClient().submitError(errorObj, {
							attribution: Attribution.INTEGRATIONS,
						});
						return null;
					});

			return jsonLdToEnhancedSmartLink(url, occurrenceIndex, response ?? null);
		}),
	);
}
